import React, { useState, useEffect } from 'react';
import { Bubble, Card } from 'tea-component';
import moment from 'moment';
import Abnormal from '@home/components/Abnormal';
import { useIntl, getLocale } from 'umi';
import TipOne from '@/pages/intelligence/assets/svg/tipicon/report-one.svg';
import TipTwo from '@/pages/intelligence/assets/svg/tipicon/report-two.svg';
import TipThree from '@/pages/intelligence/assets/svg/tipicon/report-three.svg';
import { homeHonorTopChartsNew } from '@home/services/home';
import { openPDFPreviewDownloadPage } from '@intelligence/components/ReportCard/utils';
import { setName } from '@home/pages/home/Explore/_config/common';
import { getDBIReportListPagePath, getReportResourcePath } from '@intelligence/utils/reports';
import UserDefault from '@/pages/home/assets/img/common/user-default.svg';

interface ITopPopularReportCollaborator {
  open_id?: string;
  creator_name?: string;
  creator_name_en?: string;
  creator_head_picture?: string;
}

interface ITopPopularReportAuthor {
  id: string;
  avatar?: string;
  displayName: string;
  fullName: string;
}

interface ITopPopularReportAuthorSource {
  create_head_picture?: string;
  create_name_cn?: string;
  create_name_en?: string;
  collaborators?: ITopPopularReportCollaborator[] | null;
}

export const splitReportTags = (tags?: string) => {
  if (!tags) return [];

  return tags
    .split(/[,|｜]/)
    .map((tag) => tag.trim())
    .filter(Boolean);
};

const REPORT_TAG_MAX_WIDTH = 200;
const REPORT_TAG_GAP = 10;
const REPORT_TAG_HORIZONTAL_PADDING = 16;
const REPORT_TAG_FONT = '12px Roboto, "San Francisco", "Helvetica Neue", Arial, sans-serif';
let reportTagMeasureContext: CanvasRenderingContext2D | null = null;

const getReportTagWidth = (tag: string) => {
  if (typeof document === 'undefined') {
    return tag.length * 12 + REPORT_TAG_HORIZONTAL_PADDING;
  }

  if (!reportTagMeasureContext) {
    reportTagMeasureContext = document.createElement('canvas').getContext('2d');
  }
  if (!reportTagMeasureContext) return tag.length * 12 + REPORT_TAG_HORIZONTAL_PADDING;

  reportTagMeasureContext.font = REPORT_TAG_FONT;
  return Math.ceil(reportTagMeasureContext.measureText(tag).width) + REPORT_TAG_HORIZONTAL_PADDING;
};

export const getTopPopularReportAuthors = (
  report: ITopPopularReportAuthorSource,
  isCN: boolean,
): ITopPopularReportAuthor[] => {
  const primaryFullName =
    (isCN ? report.create_name_cn : report.create_name_en) ||
    report.create_name_cn ||
    report.create_name_en ||
    '';
  const primaryAuthor: ITopPopularReportAuthor = {
    id: 'primary',
    avatar: report.create_head_picture,
    displayName: setName(primaryFullName),
    fullName: primaryFullName,
  };
  const collaborators = Array.isArray(report.collaborators)
    ? report.collaborators
    : [];

  return [
    primaryAuthor,
    ...collaborators.slice(0, 2).map((collaborator, index) => {
      const fullName =
        (isCN ? collaborator.creator_name : collaborator.creator_name_en) ||
        collaborator.creator_name ||
        collaborator.creator_name_en ||
        collaborator.open_id ||
        '';

      return {
        id: `collaborator-${collaborator.open_id || index}-${index}`,
        avatar: collaborator.creator_head_picture,
        displayName: setName(fullName),
        fullName,
      };
    }),
  ];
};

const TopPopularReports = () => {
  const locale = getLocale();
  const isCN = locale === 'zh-CN';
  const { formatMessage } = useIntl(); // 中英切换方法
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [reports, setReports] = useState([]);
  const [noData, setNoData] = useState(false);
  const splitResData = (res: any) => {
    const result = res.map((ele: any) => {
      return {
        ...ele,
        tag_cn: splitReportTags(ele?.tag_cn),
        tag_en: splitReportTags(ele?.tag_en),
      };
    });
    return result;
  };
  const getNewReports = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const startDate = moment()
        .startOf('months')
        .subtract(1, 'month')
        .format('YYYY-MM-DD');
      const endDate = moment()
        .endOf('months')
        .subtract(1, 'month')
        .format('YYYY-MM-DD');

      const res = await homeHonorTopChartsNew({
        'Top Popular Reports': {
          top: 5,
          report_types: ['intelligence_insights', 'intelligence_iic', 'gameResearch'],
          start_date: startDate,
          end_date: endDate,
        },
      });
      const data = res['Top Popular Reports'] || [];
      if (!data?.length) {
        setNoData(true);
      } else {
        const result = splitResData(data);
        setReports(result || []);
      }
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getNewReports();
  }, []);
  const handleClick = (data: any) => {
    const pdfPreviewDownloadPage = '/pdfPreviewDownloadLikeShare';
    const downloadLogKeyObject: any = {
      intelligence_insights: 'insightsReportsDownload',
      intelligence_iic: 'insightsCenterLibraryDownload',
      gameResearch: 'gameResearchDownload',
    };
    const openParms = {
      id: data?.id,
      systemId: data?.system_id,
      resourceId: data.resource_id,
      resourceName: isCN ? data?.title_cn : data?.title_en,
      resourcePath: getReportResourcePath(locale, data),
      resourceLike: data?.report_like,
      downloadLogKey: downloadLogKeyObject[data?.system_id],
      lang: isCN ? 'cn' : 'en',
      referer: getDBIReportListPagePath(data?.system_id, data?.source),
    } as any;
    openPDFPreviewDownloadPage(pdfPreviewDownloadPage, openParms);
  };
  const setTag = (item: any) => {
    const tag = (isCN ? item?.tag_cn : item?.tag_en) || [];
    const tagWidths = tag.map(getReportTagWidth);
    const totalWidth = tagWidths.reduce((sum, width) => sum + width, 0) +
      REPORT_TAG_GAP * (tag.length - 1);
    let visibleCount = tag.length;

    if (totalWidth > REPORT_TAG_MAX_WIDTH) {
      let usedWidth = 0;
      visibleCount = 0;

      tagWidths.some((width, index) => {
        const remainingCount = tag.length - index - 1;
        const moreWidth = getReportTagWidth(`+${remainingCount}`);
        const nextWidth = usedWidth + width + (index ? REPORT_TAG_GAP : 0);

        if (
          remainingCount > 0 &&
          nextWidth + REPORT_TAG_GAP + moreWidth <= REPORT_TAG_MAX_WIDTH
        ) {
          usedWidth = nextWidth;
          visibleCount = index + 1;
          return false;
        }

        return true;
      });
    }

    const tagElements = tag.slice(0, visibleCount).map((ele: any, index: number) => (
      <div key={index} className={'dbh-report-item-tag'}>
        {ele}
      </div>
    ));

    if (tag.length > visibleCount) {
      tagElements.push(
        <Bubble key="more" dark content={tag.slice(visibleCount).join(', ')}>
          <div className={'dbh-report-item-tag dbh-report-item-tag-more'}>
            +{tag.length - visibleCount}
          </div>
        </Bubble>,
      );
    }

    return tagElements;
  };
  const TopPopularReportsItem = (item: any, index: number) => {
    const tipIcon = [TipOne, TipTwo, TipThree];
    const authors = getTopPopularReportAuthors(item, isCN);
    const authorDisplayNames = authors
      .map((author) => author.displayName)
      .filter(Boolean)
      .join(', ');
    const authorFullNames = authors
      .map((author) => author.fullName)
      .filter(Boolean)
      .join(', ');
    return (
      <div
        className={'dbh-top-popular-report-item'}
        onClick={() => handleClick(item)}
      >
        <div className={'dbh-top-popular-report-rank'}>
          {index < 3 ? <img src={tipIcon[index]} /> : index + 1}
        </div>
        <img src={item?.report_picture} className={'dbh-pop-report-img'} />
        <div className={'dbh-report-item-right'}>
          <div className={'dbh-report-item-link'}>
            {isCN ? item?.title_cn : item?.title_en}
          </div>
          {item?.tag_cn?.length || item?.tag_en?.length ? (
            <div className={'dbh-report-item-desc dbh-pop-report-tag'}>
              {setTag(item)}
            </div>
          ) : null}
        </div>
        <div className={'dbh-pop-report-right-use-icon-area'}>
          <Bubble dark content={authorFullNames}>
            <div className={'dbh-pop-report-author'}>
              <div className={'dbh-pop-report-author-avatars'}>
                {authors.map((author, authorIndex) => (
                  <img
                    key={author.id}
                    src={author.avatar || UserDefault}
                    className={'dbh-report-right-user-icon'}
                    alt={author.fullName}
                    style={{ zIndex: authors.length - authorIndex }}
                  />
                ))}
              </div>
              <div className={'dbh-report-item-user-name'}>
                {authorDisplayNames}
              </div>
            </div>
          </Bubble>
        </div>
      </div>
    );
  };
  const TopPopularReportsList = () => {
    return (
      <div className={'dbh-top-popular-report'}>
        {reports.map((ele: any, index: number) =>
          TopPopularReportsItem(ele, index),
        )}
      </div>
    );
  };
  return (
    <Card className={'home-card dbh-card-height'}>
      <Card.Body
        style={{ padding: '20px 6px 10px' }}
        title={
          <div className="home-table-title dbh-home-title-padding">
            {formatMessage({ id: 'dbh_home_top_popular_reports' })}
          </div>
        }
        operation={
          <div className="home-table-time dbh-top-popluar-time dbh-home-title-padding">
            {moment().subtract(1, 'month').format('YYYY-MM')}
          </div>
        }
      >
        <Abnormal
          isLoading={loading}
          isError={error}
          isNoData={noData}
          style={{
            height: 395,
            color: '#444',
          }}
          component={<TopPopularReportsList />}
        />
      </Card.Body>
    </Card>
  );
};
export default TopPopularReports;
